import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Plus, Edit2, Trash2, Search, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Pagination, usePagination } from "../components/ui/pagination";
import { Card, CardContent } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "react-hot-toast";
import ReactQuill from "react-quill";
import Quill from "quill";
import "react-quill/dist/quill.snow.css";

const API_BASE_URL = "https://email-backend.heybuddy.co.in";

// Custom Quill configuration
const Block = Quill.import("blots/block");
Block.tagName = "DIV";
Block.className = "pre";
Quill.register(Block, true);

const TemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    content: "",
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/email-templates`);
      setTemplates(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Failed to fetch templates");
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleEditorChange = (content) => {
    setFormData({ ...formData, content });
  };

  const convertToApiFormat = (content) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");

    let apiContent = "";
    doc.body.childNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        switch (node.tagName.toLowerCase()) {
          case "h1":
            apiContent += `<h1>${node.textContent}</h1>`;
            break;
          case "h2":
            apiContent += `<h2>${node.textContent}</h2>`;
            break;
          case "h3":
            apiContent += `<h3>${node.textContent}</h3>`;
            break;
          case "p":
            apiContent += `<p>${node.innerHTML}</p>`;
            break;
          default:
            apiContent += node.outerHTML;
        }
      }
    });

    return apiContent;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const apiFormData = {
      ...formData,
      content: convertToApiFormat(formData.content),
    };
    try {
      if (currentTemplate) {
        await axios.put(
          `${API_BASE_URL}/api/email-templates/${currentTemplate._id}`,
          apiFormData
        );
        toast.success("Template updated successfully");
      } else {
        await axios.post(`${API_BASE_URL}/api/email-templates`, apiFormData);
        toast.success("Template created successfully");
      }
      setIsDialogOpen(false);
      fetchTemplates();
    } catch (error) {
      console.error("Error submitting template:", error);
      toast.error(error.response?.data?.message || "Failed to submit template");
    }
  };

  const handleEdit = async (templateId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/email-templates/${templateId}`
      );
      setCurrentTemplate(response.data);
      setFormData({
        name: response.data.name,
        content: response.data.content,
      });
      setIsDialogOpen(true);
    } catch (error) {
      console.error("Error fetching template:", error);
      toast.error("Failed to fetch template details");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this template?")) {
      try {
        await axios.delete(`${API_BASE_URL}/api/email-templates/${id}`);
        toast.success("Template deleted successfully");
        fetchTemplates();
      } catch (error) {
        console.error("Error deleting template:", error);
        toast.error("Failed to delete template");
      }
    }
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image"],
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "link",
    "image",
  ];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) => t.name?.toLowerCase().includes(q));
  }, [templates, search]);

  const { page, setPage, pageCount, total, pageSize, pageItems } =
    usePagination(filtered, 10);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold">Email Templates</h2>
          <p className="text-sm text-muted-foreground">
            Design reusable content for your campaigns.
          </p>
        </div>
        <Button
          onClick={() => {
            setCurrentTemplate(null);
            setFormData({ name: "", content: "" });
            setIsDialogOpen(true);
          }}
        >
          <Plus className="mr-2 h-4 w-4" /> Create Template
        </Button>
      </div>

      <Card>
        {/* Toolbar */}
        <div className="flex items-center gap-3 border-b p-4">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search templates…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          {!isLoading && (
            <span className="ml-auto hidden text-sm text-muted-foreground sm:block">
              {total} template{total !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Name</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 3 }).map((__, j) => (
                        <TableCell key={j}>
                          <Skeleton className="h-5 w-full max-w-[160px]" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : pageItems.length === 0 ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={3} className="py-16">
                      <div className="flex flex-col items-center justify-center text-center">
                        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                          <FileText className="h-6 w-6 text-muted-foreground" />
                        </div>
                        <p className="font-medium">
                          {search ? "No matching templates" : "No templates yet"}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {search
                            ? "Try a different search term."
                            : "Create your first template to get started."}
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  pageItems.map((template) => (
                    <TableRow key={template._id}>
                      <TableCell className="font-medium">{template.name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {template.createdAt
                          ? new Date(template.createdAt).toLocaleDateString()
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Edit"
                            onClick={() => handleEdit(template._id)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Delete"
                            className="text-muted-foreground hover:text-destructive"
                            onClick={() => handleDelete(template._id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {!isLoading && total > 0 && (
            <div className="border-t">
              <Pagination
                page={page}
                pageCount={pageCount}
                total={total}
                pageSize={pageSize}
                onPageChange={setPage}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto bg-card sm:max-w-[800px]">
          <DialogHeader>
            <DialogTitle>
              {currentTemplate ? "Edit Template" : "Create Template"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <label htmlFor="name" className="text-sm font-medium">
                  Name
                </label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="content" className="text-sm font-medium">
                  Content
                </label>
                <ReactQuill
                  theme="snow"
                  value={formData.content}
                  onChange={handleEditorChange}
                  modules={modules}
                  formats={formats}
                />
                <p className="text-sm text-muted-foreground">
                  Use <code>${"{NAME}"}</code> to include the recipient's name in
                  your template.
                </p>
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit">
                {currentTemplate ? "Update Template" : "Create Template"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplatesPage;
